// src/Pages/AdminManagment/AdminPermissionsPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { 
  useGetAdminPermissionsQuery,
  useUpdateAdminPermissionsMutation 
} from "../../redux/services/sabAdminService/admin.Services";
import { successToast, errorToast } from "../../Utils/toast";
import { FaArrowLeft, FaSave, FaSearch, FaLock, FaUnlock } from "react-icons/fa";

function AdminPermissionsPage() {
  const { adminId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [updatePermissions, { isLoading: isUpdating }] = useUpdateAdminPermissionsMutation();
  const { data: permissionsData, isLoading, refetch } = useGetAdminPermissionsQuery(adminId);
  
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminRole, setAdminRole] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    if (permissionsData?.data) {
      console.log("📝 Full API Response:", permissionsData);
      
      const allPermissions = permissionsData.data.permissions || [];
      const adminPermissions = permissionsData.data.admin_permissions || [];
      const role = permissionsData.data.role || 'sub_admin';
      
      console.log("📝 All Permissions Count:", allPermissions.length);
      console.log("📝 Admin Permissions Count:", adminPermissions.length);
      console.log("📝 Admin Permissions Data:", adminPermissions);
      
      setPermissions(allPermissions);
      
      let selectedKeys = [];
      
      if (role === 'admin' || role === 'super_admin') {
        // ✅ Admin: Select all permissions
        selectedKeys = allPermissions.map(p => p.permission_key);
        setIsSuperAdmin(true);
        console.log("✅ Admin - Selecting all permissions");
      } else {
        // ✅ Sub Admin: Match by permission_key
        // Extract permission_keys from admin_permissions
        const adminPermissionKeys = adminPermissions.map(p => p.permission_key);
        console.log("✅ Admin Permission Keys:", adminPermissionKeys);
        
        // Find matching permissions from allPermissions
        const matchedPermissions = allPermissions.filter(p => 
          adminPermissionKeys.includes(p.permission_key)
        );
        
        // Get IDs of matched permissions
        selectedKeys = matchedPermissions.map(p => p.id);
        
        setIsSuperAdmin(false);
        console.log("✅ Sub Admin - Selected IDs:", selectedKeys);
        console.log("✅ Sub Admin - Selected Count:", selectedKeys.length);
      }
      
      setSelectedPermissions(selectedKeys);
      setAdminRole(role);
      
      // Get admin name
      if (permissionsData.data.admin_name) {
        setAdminName(permissionsData.data.admin_name);
      } else {
        setAdminName(`Admin #${adminId}`);
      }
    }
  }, [permissionsData, adminId]);

  // ✅ Toggle single permission
  const handlePermissionToggle = (permissionId) => {
    if (isSuperAdmin) {
      errorToast("Admin automatically has all permissions");
      return;
    }
    
    setSelectedPermissions(prev => {
      const newSelected = prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId];
      
      console.log("📝 Toggled permission ID:", permissionId);
      console.log("📝 New selected count:", newSelected.length);
      
      return newSelected;
    });
  };

  // ✅ Toggle all permissions
  const handleSelectAll = () => {
    if (isSuperAdmin) return;
    
    if (selectedPermissions.length === permissions.length) {
      setSelectedPermissions([]);
      console.log("📝 Deselected all permissions");
    } else {
      setSelectedPermissions(permissions.map(p => p.id));
      console.log("📝 Selected all permissions");
    }
  };

  // ✅ Toggle by module
  const handleSelectModule = (moduleName) => {
    if (isSuperAdmin) return;
    
    const modulePermissions = permissions.filter(p => p.module === moduleName);
    const moduleIds = modulePermissions.map(p => p.id);
    const allSelected = moduleIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !moduleIds.includes(id)));
      console.log(`📝 Deselected module: ${moduleName}`);
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...moduleIds])]);
      console.log(`📝 Selected module: ${moduleName}`);
    }
  };

  // ✅ Save permissions
  const handleSavePermissions = async () => {
    try {
      console.log("📝 Saving permissions for admin:", adminId);
      console.log("📝 Selected IDs:", selectedPermissions);
      
      const response = await updatePermissions({
        admin_id: parseInt(adminId),
        permissions: selectedPermissions
      }).unwrap();
      
      console.log("📝 Save Response:", response);
      
      successToast(response?.message || "Permissions updated successfully");
      refetch();
    } catch (error) {
      console.error("Permission update error:", error);
      errorToast(error?.data?.message || "Failed to update permissions");
    }
  };

  // ✅ Filter permissions
  const getFilteredPermissions = () => {
    let filtered = permissions;
    
    if (searchTerm) {
      filtered = filtered.filter(permission =>
        permission.permission_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.module?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.permission_key?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (activeTab === "view") {
      filtered = filtered.filter(p => p.can_view === 1);
    } else if (activeTab === "add") {
      filtered = filtered.filter(p => p.can_add === 1);
    } else if (activeTab === "edit") {
      filtered = filtered.filter(p => p.can_edit === 1);
    } else if (activeTab === "delete") {
      filtered = filtered.filter(p => p.can_delete === 1);
    }
    
    return filtered;
  };

  // ✅ Group permissions by module
  const groupPermissionsByModule = (permissionsList) => {
    return permissionsList.reduce((acc, permission) => {
      const module = permission.module || 'Other';
      if (!acc[module]) {
        acc[module] = [];
      }
      acc[module].push(permission);
      return acc;
    }, {});
  };

  // ✅ Get permission badge
  const getPermissionBadge = (permission) => {
    const hasView = permission.can_view === 1;
    const hasAdd = permission.can_add === 1;
    const hasEdit = permission.can_edit === 1;
    const hasDelete = permission.can_delete === 1;
    
    if (hasView && hasAdd && hasEdit && hasDelete) return 'Full';
    if (hasView && hasAdd && hasEdit) return 'Edit';
    if (hasView && hasAdd) return 'Add';
    if (hasView) return 'View';
    return 'None';
  };

  const getBadgeColor = (type) => {
    switch(type) {
      case 'Full': return 'success';
      case 'Edit': return 'primary';
      case 'Add': return 'info';
      case 'View': return 'secondary';
      default: return 'danger';
    }
  };

  const filteredPermissions = getFilteredPermissions();
  const groupedPermissions = groupPermissionsByModule(filteredPermissions);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading permissions...</span>
        </div>
        <h5 className="mt-3">Loading permissions...</h5>
      </div>
    );
  }

  return (
    <div className="permissions-page" style={{ padding: '20px' }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap">
        <div>
          <h4 className="fw-semibold mb-1">
            <FaLock className="me-2 text-primary" />
            Admin Permissions
          </h4>
          <p className="text-muted mb-0">
            Manage permissions for <strong>{adminName || `Admin #${adminId}`}</strong>
            {adminRole && (
              <span className={`ms-2 badge ${adminRole === 'admin' ? 'bg-danger' : 'bg-info'}`}>
                {adminRole === 'admin' ? 'Admin' : 'Sub Admin'}
              </span>
            )}
          </p>
          <p className="text-muted small mt-1">
            <span className="text-success">✅ Selected: {selectedPermissions.length}</span> of {permissions.length} permissions
          </p>
        </div>
        <div className="mt-2 mt-md-0">
          <button
            className="btn btn-secondary me-2"
            onClick={() => navigate("/admin-list")}
          >
            <FaArrowLeft className="me-2" />
            Back to Admins
          </button>
          {!isSuperAdmin && (
            <button
              className="btn btn-primary"
              onClick={handleSavePermissions}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <><FaSave className="me-2" />Save Permissions</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Admin Notice */}
      {isSuperAdmin && (
        <div className="alert alert-info mb-4">
          <FaUnlock className="me-2" />
          <strong>Admin User</strong> - This user automatically has all permissions.
        </div>
      )}

      {/* Search and Controls */}
      <div className="permissions-controls mb-4">
        <div className="row align-items-center">
          <div className="col-md-4">
            <div className="search-wrapper" style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }} />
              <input
                type="text"
                className="form-control"
                style={{ paddingLeft: '35px' }}
                placeholder="Search permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="btn-group w-100" role="group">
              <button 
                className={`btn btn-outline-secondary ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                All ({permissions.length})
              </button>
              <button 
                className={`btn btn-outline-secondary ${activeTab === 'view' ? 'active' : ''}`}
                onClick={() => setActiveTab('view')}
              >
                View
              </button>
              <button 
                className={`btn btn-outline-secondary ${activeTab === 'add' ? 'active' : ''}`}
                onClick={() => setActiveTab('add')}
              >
                Add
              </button>
              <button 
                className={`btn btn-outline-secondary ${activeTab === 'edit' ? 'active' : ''}`}
                onClick={() => setActiveTab('edit')}
              >
                Edit
              </button>
              <button 
                className={`btn btn-outline-secondary ${activeTab === 'delete' ? 'active' : ''}`}
                onClick={() => setActiveTab('delete')}
              >
                Delete
              </button>
            </div>
          </div>
          <div className="col-md-4 text-md-end mt-3 mt-md-0">
            {!isSuperAdmin && permissions.length > 0 && (
              <>
                <button
                  className="btn btn-outline-primary me-2"
                  onClick={handleSelectAll}
                >
                  {selectedPermissions.length === permissions.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-muted small">
                  ({selectedPermissions.length} of {permissions.length})
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Permissions Grid */}
      <div className="permissions-grid">
        {Object.keys(groupedPermissions).length === 0 ? (
          <div className="text-center py-5">
            {searchTerm ? (
              <p className="text-muted">No permissions found matching "{searchTerm}"</p>
            ) : (
              <p className="text-muted">No permissions available</p>
            )}
          </div>
        ) : (
          Object.keys(groupedPermissions).sort().map((module) => {
            const modulePermissions = groupedPermissions[module];
            const moduleIds = modulePermissions.map(p => p.id);
            const selectedCount = modulePermissions.filter(p => selectedPermissions.includes(p.id)).length;
            const allSelected = moduleIds.every(id => selectedPermissions.includes(id));
            
            return (
              <div key={module} className="card card-custum mb-4">
                <div className="card-header d-flex justify-content-between align-items-center ">
                  <h5 className="mb-0">
                    <span className="badge badge-primary-soft">
                      {modulePermissions.length}
                    </span>
                    {module}
                    <span className="ms-2  small">
                      ({selectedCount} selected)
                    </span>
                  </h5>
                  {!isSuperAdmin && (
                    <button
                      className="btn-module"
                      onClick={() => handleSelectModule(module)}
                    >
                      {allSelected ? 'Deselect Module' : 'Select Module'}
                    </button>
                  )}
                </div>
                <div className="card-body">
                  <div className="row">
                    {modulePermissions.map((permission) => {
                      // ✅ Check if this permission is selected by ID
                      const isSelected = selectedPermissions.includes(permission.id);
                      
                      return (
                        <div key={permission.id} className="col-md-6 col-lg-4 mb-2">
                          <label 
                            className={`d-flex align-items-center p-2 border rounded ${isSuperAdmin ? 'opacity-50' : ''} ${isSelected ? 'bg-primary bg-opacity-10 border-primary' : ''}`}
                            style={{ cursor: isSuperAdmin ? 'not-allowed' : 'pointer' }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handlePermissionToggle(permission.id)}
                              disabled={isSuperAdmin}
                              className="me-2"
                              style={{ width: '18px', height: '18px' }}
                            />
                            <div>
                              <div className="fw-semibold">
                                {permission.permission_name}
                                <span className={`badge bg-${getBadgeColor(getPermissionBadge(permission))} ms-2`}>
                                  {getPermissionBadge(permission)}
                                </span>
                              </div>
                              <div className="text-muted small">
                                <code>{permission.permission_key}</code>
                              </div>
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="permissions-footer mt-4 pt-3 border-top">
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="text-muted small">
            <span className="me-3">
              <span className="badge bg-success me-1">Full</span> Full Access
            </span>
            <span className="me-3">
              <span className="badge bg-primary me-1">Edit</span> Edit Access
            </span>
            <span className="me-3">
              <span className="badge bg-info me-1">Add</span> Add Access
            </span>
            <span className="me-3">
              <span className="badge bg-secondary me-1">View</span> View Only
            </span>
          </div>
          {!isSuperAdmin && permissions.length > 0 && (
            <button
              className="btn btn-primary btn-lg"
              onClick={handleSavePermissions}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <><FaSave className="me-2" />Save Permissions</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPermissionsPage;